import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, ArrowLeft, Play, Mic, Square, Send, Heart, Loader2, Trophy, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { battlePrompts, battleList, battleSubmit, battleVote, renderWithEffect, voicePresets, battlePrizePool } from "@/lib/voiceApi";

export default function VoiceBattles() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [sort, setSort] = useState("top");
  const [showRecord, setShowRecord] = useState(false);
  const [presets, setPresets] = useState([]);
  const [prizePool, setPrizePool] = useState(null);
  const audioRefs = useRef({});

  useEffect(() => {
    document.body.classList.add("voice-scope");
    return () => document.body.classList.remove("voice-scope");
  }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/voiceification/battles"); return; }
    battlePrompts().then((r) => { setPrompts(r.prompts || []); setSelectedPromptId(r.featured_id); });
    voicePresets().then((r) => setPresets(r.presets || []));
    battlePrizePool().then(setPrizePool).catch(() => {});
  }, [user, loading, nav]);
  useEffect(() => {
    if (selectedPromptId === null) return;
    battleList({ prompt_id: selectedPromptId, sort }).then((r) => setSubmissions(r.submissions || [])).catch(() => {});
    // Refresh pool when the featured prompt is selected (keeps leaderboard live)
    battlePrizePool().then(setPrizePool).catch(() => {});
  }, [selectedPromptId, sort]);

  const currentPrompt = prompts.find((p) => p.id === selectedPromptId);
  const vote = async (s) => {
    try {
      const r = await battleVote(s.id);
      setSubmissions((list) => list.map((x) => x.id === s.id ? { ...x, voted_by_me: r.voted, votes: x.votes + (r.voted ? 1 : -1) } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't vote"); }
  };

  return (
    <div data-testid="voice-battles" className="min-h-screen bg-gradient-to-b from-[#1A0033] via-[#0A0018] to-black text-white pb-20">
      <header className="sticky top-0 z-40 bg-[#0A0018]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-2">
          <Link to="/voiceification" className="p-2 rounded-full hover:bg-white/5"><ArrowLeft className="w-4 h-4" /></Link>
          <Swords className="w-5 h-5 text-[#FFD700]" />
          <div className="font-black">Voice Battles</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 space-y-4">
        <section className="rounded-2xl bg-gradient-to-br from-[#FFD700]/20 to-[#7C3AED]/20 border border-[#FFD700]/40 p-5">
          <div className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"><Trophy className="w-3 h-3 text-[#FFD700]" /> Today's featured battle</div>
          <div className="font-black text-xl">{currentPrompt?.text || "…"}</div>
          <div className="text-xs text-white/60 mt-1">Pick any voice, record your take, and let the community vote.</div>
          <button data-testid="battles-submit-btn" onClick={() => setShowRecord(true)}
            className="mt-3 inline-flex items-center gap-1 bg-[#FFD700] text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full">
            <Mic className="w-3.5 h-3.5" /> Record my take
          </button>
        </section>

        {/* Daily Prize Pool */}
        {prizePool && (
          <section data-testid="battles-prize-pool"
            className="rounded-2xl bg-gradient-to-br from-[#39FF14]/15 via-[#FFD700]/10 to-transparent border border-[#39FF14]/40 p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1.5 bg-black/40 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/50">
                  <Coins className="w-3 h-3" fill="currentColor" /> Daily prize pool
                </div>
                <div className="font-black text-2xl" data-testid="prize-pool-total">{prizePool.total_pool_coins} 🪙</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">
                Auto-paid at 00:00 UTC · {prizePool.top?.length || 0} contender{(prizePool.top?.length || 0) === 1 ? "" : "s"}
              </div>
            </div>

            {/* Rank tiers */}
            <div className="grid grid-cols-3 gap-2 mt-3">
              {(prizePool.pool || []).map((coins, i) => {
                const winner = prizePool.top?.[i];
                const medal = ["#FFD700", "#C0C0C0", "#B45309"][i] || "#FFD700";
                return (
                  <div key={i} data-testid={`prize-rank-${i + 1}`}
                    className={"rounded-xl border p-3 text-center transition-colors " + (winner?.is_me ? "border-[#FF00E5] bg-[#FF00E5]/10" : "border-white/10 bg-black/30")}>
                    <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: medal }}>
                      #{i + 1}{i === 0 ? " · Gold" : i === 1 ? " · Silver" : " · Bronze"}
                    </div>
                    <div className="font-black text-lg mt-0.5" style={{ color: medal }}>{coins} 🪙</div>
                    {winner ? (
                      <div className="mt-1 flex items-center justify-center gap-1.5 min-w-0">
                        <div className="w-4 h-4 rounded-full shrink-0" style={{ background: winner.avatar_color }} />
                        <div className="text-[10px] truncate font-bold">
                          {winner.is_me ? "You" : winner.user_name || "—"}
                        </div>
                        <div className="text-[10px] text-white/50">· {winner.votes}♥</div>
                      </div>
                    ) : (
                      <div className="mt-1 text-[10px] text-white/40">Slot open</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Yesterday's winners */}
            {prizePool.yesterday?.winners?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10" data-testid="prize-yesterday">
                <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Yesterday's payouts</div>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {prizePool.yesterday.winners.map((w) => (
                    <span key={w.submission_id || `${w.rank}-${w.user_id}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                      <Trophy className="w-3 h-3 text-[#FFD700]" /> #{w.rank} {w.user_name || "—"} · {w.coins_awarded}🪙
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        <div className="flex gap-2 overflow-x-auto pb-1">
          {prompts.map((p) => (
            <button key={p.id} data-testid={`prompt-${p.id}`} onClick={() => setSelectedPromptId(p.id)}
              className={"whitespace-nowrap px-3 py-1.5 rounded-full text-[11px] font-bold " + (selectedPromptId === p.id ? "bg-[#FFD700] text-black" : "bg-white/5 border border-white/10 text-white/70 hover:border-white/30")}>
              #{p.id + 1}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          {[{ id: "top", label: "Top" }, { id: "new", label: "New" }].map((s) => (
            <button key={s.id} data-testid={`battles-sort-${s.id}`} onClick={() => setSort(s.id)}
              className={"px-3 py-1 rounded-full text-xs font-bold " + (sort === s.id ? "bg-white text-black" : "bg-white/5 border border-white/10 text-white/70")}>
              {s.label}
            </button>
          ))}
        </div>

        {submissions.length === 0 ? (
          <div data-testid="battles-empty" className="text-center py-10 bg-white/5 rounded-2xl border border-white/10 text-white/50 text-sm">
            No submissions yet — be first to enter this battle!
          </div>
        ) : (
          <ul className="space-y-2">
            {submissions.map((s, i) => (
              <li key={s.id} data-testid={`battle-sub-${s.id}`}
                className={"rounded-2xl border p-4 " + (i === 0 ? "bg-gradient-to-r from-[#FFD700]/15 to-transparent border-[#FFD700]/40" : "bg-white/5 border-white/10")}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center font-black text-xs" style={{ background: s.avatar_color }}>{(s.user_name || "?")[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate">{s.user_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">Voice: {s.voice_slug}</div>
                  </div>
                  {i < 3 && <Trophy className={"w-4 h-4 " + (i === 0 ? "text-[#FFD700]" : i === 1 ? "text-white/70" : "text-[#B45309]")} />}
                </div>
                <audio ref={(el) => { if (el) audioRefs.current[s.id] = el; }} src={s.audio_url} controls className="w-full mt-2 rounded" />
                <div className="mt-3 flex items-center">
                  <button data-testid={`battle-vote-${s.id}`} onClick={() => vote(s)}
                    className={"inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-colors " + (s.voted_by_me ? "bg-[#FF00E5] text-white" : "bg-white/5 text-white/70 hover:bg-white/10")}>
                    <Heart className="w-3.5 h-3.5" fill={s.voted_by_me ? "currentColor" : "none"} /> {s.votes}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>

      {showRecord && currentPrompt && (
        <SubmitModal presets={presets} prompt={currentPrompt}
          onClose={() => setShowRecord(false)}
          onSubmitted={(sub) => { setShowRecord(false); setSubmissions((l) => [sub, ...l]); toast.success("Submitted — good luck!"); }} />
      )}
    </div>
  );
}

function SubmitModal({ prompt, presets, onClose, onSubmitted }) {
  const [preset, setPreset] = useState(presets[0]);
  const [recording, setRecording] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [outBlob, setOutBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mrRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null); const timerRef = useRef(null);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const raw = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach((t) => t.stop());
        setBusy(true);
        try {
          const wav = await renderWithEffect(raw, preset.cfg || {});
          setOutBlob(wav);
          setPreviewUrl(URL.createObjectURL(wav));
        } catch { toast.error("Rendering failed"); }
        finally { setBusy(false); }
      };
      mr.start(); mrRef.current = mr; setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => { if (s + 1 >= 20) { stopRec(); return s + 1; } return s + 1; }), 1000);
    } catch { toast.error("Mic blocked"); }
  };
  const stopRec = () => {
    if (mrRef.current && mrRef.current.state !== "inactive") mrRef.current.stop();
    setRecording(false); if (timerRef.current) clearInterval(timerRef.current);
  };
  const submit = async () => {
    if (!outBlob) return;
    setBusy(true);
    try {
      // Convert to data URL so backend can hand it back (Phase B storage; Phase C = object storage)
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result;
        try {
          const r = await battleSubmit({ prompt_id: prompt.id, voice_slug: preset.slug, audio_url: dataUrl, duration_sec: seconds });
          onSubmitted?.(r.submission);
        } catch (err) { toast.error(err.response?.data?.detail || "Couldn't submit"); }
        finally { setBusy(false); }
      };
      reader.readAsDataURL(outBlob);
    } catch { setBusy(false); toast.error("Failed"); }
  };

  return (
    <div data-testid="battles-submit-modal" className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-[#0A0018] rounded-t-3xl sm:rounded-2xl p-5 space-y-3 border border-white/10">
        <div className="font-black">{prompt.text}</div>
        <div className="text-[10px] uppercase tracking-widest text-white/50">Pick a voice</div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {presets.slice(0, 20).map((p) => (
            <button key={p.slug} data-testid={`submit-voice-${p.slug}`} onClick={() => setPreset(p)}
              className={"shrink-0 px-3 py-1.5 rounded-full text-xs font-bold " + (preset?.slug === p.slug ? "bg-[#FF00E5] text-white" : "bg-white/5 border border-white/10 text-white/70")}>
              {p.emoji} {p.name}
            </button>
          ))}
        </div>
        <div className="text-center">
          <button data-testid={recording ? "submit-stop" : "submit-record"} onClick={recording ? stopRec : startRec}
            className={"w-20 h-20 rounded-full mx-auto flex items-center justify-center " + (recording ? "bg-[#FF3B30] animate-pulse" : "bg-gradient-to-br from-[#FF00E5] to-[#7C3AED]")}>
            {recording ? <Square className="w-8 h-8 text-white" fill="white" /> : <Mic className="w-8 h-8 text-white" fill="white" />}
          </button>
          <div className="text-xs text-white/50 mt-1">{recording ? `${seconds}s / 20s` : "Tap to record"}</div>
        </div>
        {busy && !previewUrl && <div className="text-center text-white/70 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Rendering…</div>}
        {previewUrl && <audio src={previewUrl} controls className="w-full rounded" data-testid="submit-preview" />}
        <button data-testid="submit-post" disabled={!outBlob || busy} onClick={submit}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD700] text-black font-black py-3 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Submit to battle</>}
        </button>
      </div>
    </div>
  );
}
